// Fetches live bank + credit-card balances from QuickBooks (via the
// api/qb/sync?report=accounts endpoint) and maps the six named Profit First
// accounts to buckets. Anything unrecognized (e.g. the unused Chase checking)
// is ignored; credit-card accounts are summed as the card debt.

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/apiFetch';

// QB account name (upper-cased, trimmed) -> Profit First bucket key.
const BUCKET_BY_NAME = {
  INCOME: 'income',
  OPEX: 'opex',
  'OWNERS COMP': 'owner',
  "OWNER'S COMP": 'owner',
  PROFIT: 'profit',
  TAX: 'tax',
  VAULT: 'vault',
};

const EMPTY_BUCKETS = { income: 0, opex: 0, owner: 0, tax: 0, profit: 0, vault: 0 };

export function useQbAccounts() {
  const [state, setState] = useState({
    loading: true, error: null, buckets: null, cards: [], cardTotal: 0, asOf: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await apiFetch('/api/qb/sync?report=accounts');
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load accounts');
      const buckets = { ...EMPTY_BUCKETS };
      const cards = [];
      for (const a of json.data.accounts || []) {
        if (a.type === 'credit_card') { cards.push(a); continue; }
        const key = BUCKET_BY_NAME[String(a.name || '').toUpperCase().trim()];
        if (key) buckets[key] = a.balance;
      }
      const cardTotal = cards.reduce((s, c) => s + Math.abs(Number(c.balance) || 0), 0);
      setState({ loading: false, error: null, buckets, cards, cardTotal, asOf: json.data.asOf });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message, buckets: null }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}
